import {
	AfterViewInit,
	ChangeDetectionStrategy,
	Component,
	computed,
	effect,
	ElementRef,
	inject,
	input,
	OnDestroy,
	output,
	signal,
	untracked,
	viewChild,
	ViewEncapsulation,
} from '@angular/core';
import { FocusTrap, FocusTrapFactory } from '@angular/cdk/a11y';
import {
	BlockScrollStrategy,
	ScrollStrategyOptions,
} from '@angular/cdk/overlay';
import { CdkScrollableModule } from '@angular/cdk/scrolling';
import { MatIconModule } from '@angular/material/icon';
import { Editor } from '@tiptap/core';
import { StarterKit } from '@tiptap/starter-kit';
import { jeptoRichMarkdownExtensions } from '../../../../utils/tiptap-markdown-extensions.util';
import type { CardStyle, Analysis } from '../../../../models/card-model';
import type { ThemeConfig } from '../../../../models/theme.model';
import type { ThemeMode } from '../../../../services/theme.service';
import { DEFAULT_THEME } from '../../../../defaults/theme.defaults';
import { BUILT_IN_PALETTES } from '../../../../defaults/palette.default';
import { PaletteService } from '../../../../services/palette.service';
import { ThemeService } from '../../../../services/theme.service';
import { applyElementThemeVariables } from '../../../../utils/apply-element-theme-variables.util';
import { formatAnalysisRangeLongMonthYear } from '../../../../utils/analysis-datetime.util';
import { getAnalysisPickerLabel } from '../../../../utils/analysis-label.util';
import {
	analysisKey,
	analysisKeysMatch,
} from '../../../../utils/analysis.util';

/**
 * Analysis Drawer Component
 *
 * A right-side panel that slides in to display the full analysis for a selected period.
 * Content is rendered via TipTap in read-only mode — the same rendering
 * engine as the markdown card, so styles are consistent.
 *
 * Accessibility:
 * - role="dialog" aria-modal="true"
 * - Focus moves to close button on open; returns to trigger on close
 * - Tab focus is trapped while the drawer is open
 * - Escape key closes the drawer
 */
@Component({
	selector: 'jepto-analysis-drawer',
	templateUrl: './jepto-analysis-drawer.component.html',
	styleUrl: './jepto-analysis-drawer.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
	imports: [CdkScrollableModule, MatIconModule],
	host: {
		class: 'jepto-analysis-drawer',
		'[class.jepto-analysis-drawer--contained]':
			'overlayMode() === "contained"',
		'[attr.aria-hidden]': '!isOpen()',
		'(document:keydown.escape)': 'onEscapeKey($event)',
	},
})
export class JeptoAnalysisDrawerComponent implements AfterViewInit, OnDestroy {
	// ── Inputs ────────────────────────────────────────────────────────────────

	readonly testId = input<string | undefined>();

	/** Ordered analysis periods shown in drawer tabs. */
	analyses = input<Analysis[]>([]);

	/** Active period id selected in the card. */
	activePeriodId = input<string | undefined>(undefined);

	/** Whether the drawer is open. */
	isOpen = input<boolean>(false);

	/** `viewport` = full-screen overlay (grid/dashboard). `contained` = clip to transformed preview pane. */
	overlayMode = input<'viewport' | 'contained'>('viewport');

	/** Optional CSS width (e.g. `min(100%, 360px)`). Overrides `--jepto-analysis-drawer-panel-width`. */
	panelWidth = input<string | undefined>(undefined);

	/** Optional portal target for contained overlays (editor preview column). */
	overlayHost = input<HTMLElement | null>(null);

	/** Trigger element to return focus to on close. */
	triggerElement = input<HTMLElement | null>(null);

	/** Preview / layout theme mode for scoped drawer styling. */
	themeMode = input<ThemeMode>('auto');

	/** Theme configuration for drawer CSS variables. */
	themeConfig = input<ThemeConfig | undefined>(undefined);

	/** Palette id when it differs from {@link themeConfig}.palette. */
	palette = input<string | undefined>(undefined);

	/** Optional card style overrides (editor preview). */
	cardStyle = input<CardStyle | undefined>(undefined);

	// ── Outputs ───────────────────────────────────────────────────────────────

	readonly closed = output<void>();
	readonly periodSelected = output<string>();

	// ── Template refs ─────────────────────────────────────────────────────────

	private panelRef = viewChild<ElementRef<HTMLElement>>('drawerPanel');
	private closeBtnRef = viewChild<ElementRef<HTMLButtonElement>>('closeBtn');
	private overlayRootRef = viewChild<ElementRef<HTMLElement>>('overlayRoot');
	private editorContainer =
		viewChild<ElementRef<HTMLDivElement>>('editorContainer');

	private originalPortalParent: HTMLElement | null = null;
	private originalPortalNextSibling: ChildNode | null = null;

	// ── Computed ──────────────────────────────────────────────────────────────

	protected activePeriod = computed<Analysis | null>(() => {
		const periods = this.analyses();
		if (!periods.length) return null;

		const activeId = this.activePeriodId();
		if (activeId) {
			const period = periods.find(
				item => analysisKey(item) === activeId
			);
			if (period) return period;
		}

		return periods[0] ?? null;
	});

	protected formattedPeriod = computed(() => {
		const period = this.activePeriod();
		if (!period) return '';
		return formatAnalysisRangeLongMonthYear(
			period.startTimestamp,
			period.endTimestamp
		);
	});

	protected activePeriodTitle = computed(() => {
		const period = this.activePeriod();
		if (!period) return '';
		return period.label?.trim() || this.formattedPeriod();
	});

	private readonly resolvedAppearanceMode = computed((): 'light' | 'dark' => {
		const mode = this.themeMode();
		if (mode === 'light' || mode === 'dark') {
			return mode;
		}
		return this.themeService.getActiveThemeModeSignal()();
	});

	private readonly effectiveThemeConfig = computed(
		() => this.themeConfig() ?? DEFAULT_THEME
	);

	private readonly effectivePaletteId = computed(
		() => this.palette() ?? this.effectiveThemeConfig().palette
	);

	private readonly paletteColorsForTheme = computed(() => {
		if (this.paletteService.getAvailablePalettesSignal()().length === 0) {
			this.paletteService.setAvailablePalettes(BUILT_IN_PALETTES);
		}
		const paletteId = this.effectivePaletteId();
		const paletteConfig = this.paletteService.getPaletteById(paletteId);
		const activeMode = this.resolvedAppearanceMode();
		if (paletteConfig) {
			return activeMode === 'dark'
				? paletteConfig.dark
				: paletteConfig.light;
		}
		return this.paletteService.getColorsForPalette(paletteId);
	});

	// ── CDK services ──────────────────────────────────────────────────────────

	private focusTrapFactory = inject(FocusTrapFactory);
	private readonly paletteService = inject(PaletteService);
	private readonly themeService = inject(ThemeService);
	private scrollStrategy: BlockScrollStrategy = inject(
		ScrollStrategyOptions
	).block();

	// ── Internal state ────────────────────────────────────────────────────────

	private editor: Editor | null = null;
	private focusTrap: FocusTrap | null = null;
	private editorInitialized = signal(false);

	// ── Lifecycle ─────────────────────────────────────────────────────────────

	constructor() {
		// When drawer opens: lock body scroll, trap focus; on close: restore
		effect(() => {
			const viewportOverlay = this.overlayMode() === 'viewport';
			if (this.isOpen()) {
				if (viewportOverlay) {
					this.scrollStrategy.enable();
				}
				requestAnimationFrame(() => {
					const panel = this.panelRef()?.nativeElement;
					if (!panel) return;

					this.focusTrap = this.focusTrapFactory.create(panel);

					const closeBtn = this.closeBtnRef()?.nativeElement;
					if (closeBtn) {
						closeBtn.focus({ preventScroll: true });
					} else {
						this.focusTrap.focusInitialElementWhenReady();
					}
				});
			} else {
				if (viewportOverlay) {
					this.scrollStrategy.disable();
				}
				this.focusTrap?.destroy();
				this.focusTrap = null;
				this.triggerElement()?.focus({ preventScroll: true });
			}
		});

		// Keep editor content in sync with input — guarded by editorInitialized
		// so this never fires before ngAfterViewInit creates the editor instance.
		effect(() => {
			const md = this.activePeriod()?.content || '';
			if (this.editorInitialized() && this.editor) {
				this.editor.commands.setContent(md);
			}
		});

		effect(() => {
			const mode = this.overlayMode();
			const open = this.isOpen();
			const host = this.overlayHost();
			const root = this.overlayRootRef()?.nativeElement;

			untracked(() => {
				if (!root) {
					return;
				}

				if (!open) {
					if (root.parentElement === document.body) {
						this.#restorePortal();
					}
					return;
				}

				if (mode === 'contained' && host) {
					this.#mountPortal(host, root);
					return;
				}

				if (mode === 'viewport') {
					this.#mountPortal(document.body, root);
				}
			});
		});

		effect(() => {
			const root = this.overlayRootRef()?.nativeElement;
			if (!root) return;

			applyElementThemeVariables({
				element: root,
				themeConfig: this.effectiveThemeConfig(),
				activeMode: this.resolvedAppearanceMode(),
				paletteColors: this.paletteColorsForTheme(),
				cardStyle: this.cardStyle(),
			});
			root.classList.remove('theme-light', 'theme-dark');
			root.classList.add(`theme-${this.resolvedAppearanceMode()}`);
		});
	}

	ngAfterViewInit(): void {
		this.initEditor();
		this.editorInitialized.set(true);
	}

	ngOnDestroy(): void {
		this.#restorePortal();
		this.scrollStrategy.disable();
		this.focusTrap?.destroy();
		if (this.editor) {
			this.editor.destroy();
			this.editor = null;
		}
	}

	#mountPortal(host: HTMLElement, root: HTMLElement): void {
		if (root.parentElement === host) return;

		if (!this.originalPortalParent) {
			this.originalPortalParent = root.parentElement;
			this.originalPortalNextSibling = root.nextSibling;
		}

		host.appendChild(root);
	}

	#restorePortal(): void {
		const root = this.overlayRootRef()?.nativeElement;
		if (!root || !this.originalPortalParent) return;
		if (root.parentElement === this.originalPortalParent) return;

		if (this.originalPortalNextSibling) {
			this.originalPortalParent.insertBefore(
				root,
				this.originalPortalNextSibling
			);
		} else {
			this.originalPortalParent.appendChild(root);
		}
	}

	// ── Editor ────────────────────────────────────────────────────────────────

	private initEditor(): void {
		const container = this.editorContainer()?.nativeElement;
		if (!container || this.editor) return;

		this.editor = new Editor({
			element: container,
			extensions: [StarterKit, ...jeptoRichMarkdownExtensions()],
			content: this.activePeriod()?.content || '',
			editable: false,
		});
	}

	// ── Event handlers ────────────────────────────────────────────────────────

	protected onScrimClick(): void {
		this.closed.emit();
	}

	protected onCloseClick(event: MouseEvent): void {
		event.stopPropagation();
		this.closed.emit();
	}

	protected onPanelClick(event: MouseEvent): void {
		event.stopPropagation();
	}

	protected periodKey(period: Analysis): string {
		return analysisKey(period);
	}

	protected isActivePeriod(period: Analysis): boolean {
		const active = this.activePeriod();
		return !!active && analysisKeysMatch(active, period);
	}

	protected onPeriodTabClick(periodKey: string): void {
		this.periodSelected.emit(periodKey);
	}

	protected periodLabel(period: Analysis, index: number): string {
		return getAnalysisPickerLabel(period, index);
	}

	protected onEscapeKey(event: KeyboardEvent): void {
		if (!this.isOpen()) return;
		event.preventDefault();
		this.closed.emit();
	}
}
